import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, qs } from "@/lib/api";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Users, Flame, LayoutGrid, Smartphone, UserCheck, TrendingUp, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta, useLive } from "@/lib/site";
import { stageApi } from "@/lib/stageApi";
import { PostCard, PostTile, CreatorAvatar, VerifiedMark } from "@/components/stage/PostCard";
import { NewPostsPill } from "@/pages/stage/Social";

const TABS = [
  { key: "foryou", label: "For you", icon: Flame },
  { key: "following", label: "Following", icon: Users, auth: true },
  { key: "friends", label: "Friends", icon: UserCheck, auth: true, surface: true },
  { key: "trends", label: "Trends", icon: TrendingUp, auth: true, surface: true },
  { key: "nearby", label: "Nearby", icon: MapPin, auth: true, surface: true },
  { key: "explore", label: "Explore", icon: LayoutGrid },
];

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${active ? "border-transparent bg-foreground text-background" : "border-border bg-card text-foreground/80 hover:bg-muted"}`}
    >
      {children}
    </button>
  );
}

function FeedSkeleton({ grid }) {
  return grid ? (
    <div className="grid grid-cols-3 gap-1.5 sm:gap-2">{Array.from({ length: 9 }).map((_, i) => <Skeleton key={i} className="aspect-square rounded-xl" />)}</div>
  ) : (
    <div className="space-y-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-56 rounded-2xl" />)}</div>
  );
}

function usePaged(fetchPage, deps) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [next, setNext] = useState(null);
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");
  const seq = useRef(0);

  const load = useCallback(
    async (pg, replace) => {
      const my = ++seq.current;
      setState(replace ? "loading" : "more");
      setError("");
      try {
        const d = await fetchPage(pg);
        if (my !== seq.current) return;
        const fresh = Array.isArray(d?.items) ? d.items : [];
        setItems((prev) => {
          const base = replace ? [] : prev;
          const seen = new Set(base.map((x) => x.id));
          return [...base, ...fresh.filter((x) => !seen.has(x.id))];
        });
        setNext(d?.next_page ?? null);
        setPage(pg);
        setState("idle");
      } catch (e) {
        if (my !== seq.current) return;
        setError(e?.message || "Couldn't load the feed.");
        setState("idle");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps,
  );

  useEffect(() => {
    load(0, true);
  }, [load]);

  return { items, state, error, hasMore: next !== null && next !== undefined && next !== false, loadMore: () => load(page + 1, false), reload: () => load(0, true) };
}

export default function StageFeed() {
  usePageMeta("Pulse — the Stage feed", "What creators on Fable + are posting right now: Pulse posts, Reels and the people behind the bookings. Follow, like and save from the web; post from the app.");
  const { user, bootstrapped } = useAuth();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab") || "foryou";
  const category = params.get("category") || "";
  const q = params.get("q") || "";
  const tab = TABS.some((t) => t.key === tabParam && t.auth) && !user ? "foryou" : TABS.some((t) => t.key === tabParam) ? tabParam : "foryou";
  const grid = tab === "explore" || !!q;
  const [search, setSearch] = useState(q);
  const config = useLive(() => stageApi.config(), []);
  const trending = useLive(() => stageApi.trending(), []);
  const creators = useLive(() => stageApi.creators({ sort: "followers", limit: 6 }), []);

  const set = (patch) => {
    const n = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)));
    setParams(n, { replace: true });
  };

  const surfaceCursor = useRef({});
  const fetchPage = useCallback(
    (pg) => {
      if (q) return stageApi.explore({ q, page: pg, limit: 24 });
      if (tab === "explore") return stageApi.explore({ topic: category || undefined, page: pg, limit: 24 });
      if (TABS.some((t) => t.key === tab && t.surface)) {
        // Same mixer the app uses: Friends (people you both follow), Trends (72-hour heat), Nearby (your city).
        const cur = pg === 0 ? undefined : surfaceCursor.current[tab + ":" + pg];
        return api("/stage/feed?" + qs({ surface: tab, cursor: cur, limit: 12 })).then((d) => {
          if (d && d.next_cursor) surfaceCursor.current[tab + ":" + (pg + 1)] = d.next_cursor;
          return { items: (d && d.items) || [], next_page: d && d.next_cursor ? pg + 1 : null };
        });
      }
      return stageApi.feed({ tab, page: pg, limit: 12, category: category || undefined });
    },
    [tab, category, q],
  );
  const feed = usePaged(fetchPage, [fetchPage, bootstrapped, user?.id]);
  const loadedAt = useMemo(() => new Date().toISOString(), [feed.items.length === 0]); // eslint-disable-line react-hooks/exhaustive-deps

  // Count views for signed-in members (same signal the app sends), once per batch.
  const counted = useRef(new Set());
  useEffect(() => {
    if (!user || grid) return;
    const ids = feed.items.map((p) => p.id).filter((id) => !counted.current.has(id));
    if (!ids.length) return;
    ids.forEach((id) => counted.current.add(id));
    stageApi.view(ids.slice(0, 50));
  }, [feed.items, user, grid]);

  const categories = useMemo(() => (config && Array.isArray(config.categories) ? config.categories : []), [config]);

  const submitSearch = (e) => {
    e.preventDefault();
    set({ q: search.trim(), tab: search.trim() ? "" : tab });
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--fable)" }}>Stage</p>
          <h1 className="mt-1 font-serif-display text-3xl font-bold sm:text-4xl">{(config && config.feed_name) || "Pulse"}</h1>
          <p className="mt-1 text-muted-foreground">Posts from the creators, hosts and pros you can book, in one feed.</p>
        </div>
        <form onSubmit={submitSearch} className="flex w-full max-w-md items-center gap-2" role="search">
          <label className="sr-only" htmlFor="stage-q">Search posts, hashtags and creators</label>
          <div className="relative flex-1">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input id="stage-q" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search posts, #tags, creators" data-testid="stage-search" className="w-full rounded-full border border-border bg-card py-2 pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
          </div>
          <Button type="submit" variant="outline" className="rounded-full">Search</Button>
        </form>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2 border-b border-border pb-3">
        {TABS.filter((t) => !t.auth || user).map((t) => (
          <Chip key={t.key} active={!q && tab === t.key} onClick={() => set({ tab: t.key, q: "" })} testid={`stage-tab-${t.key}`}>
            <span className="inline-flex items-center gap-1.5"><t.icon size={14} /> {t.label}</span>
          </Chip>
        ))}
        {!user && bootstrapped && (
          <Link to="/login" state={{ from: "/stage/feed?tab=following" }} className="text-sm text-muted-foreground hover:underline">Sign in for your Following feed</Link>
        )}
      </div>

      {categories.length > 0 && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="stage-categories">
          <Chip active={!category} onClick={() => set({ category: "" })}>All</Chip>
          {categories.map((c) => (
            <Chip key={c.key} active={category === c.key} onClick={() => set({ category: category === c.key ? "" : c.key })} testid={`stage-cat-${c.key}`}>{c.label}</Chip>
          ))}
        </div>
      )}

      <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div>
          {q && (
            <p className="mb-3 text-sm text-muted-foreground">
              Results for <span className="font-semibold text-foreground">{q}</span> · <button type="button" className="underline" onClick={() => { setSearch(""); set({ q: "" }); }}>clear</button>
            </p>
          )}
          {feed.state === "loading" ? (
            <FeedSkeleton grid={grid} />
          ) : feed.error ? (
            <div className="rounded-2xl border border-border bg-card p-8 text-center">
              <p className="font-semibold">Couldn't load the feed</p>
              <p className="mt-1 text-sm text-muted-foreground">{feed.error}</p>
              <Button variant="outline" className="mt-4 rounded-full" onClick={feed.reload}>Try again</Button>
            </div>
          ) : feed.items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-10 text-center">
              <p className="font-semibold">{tab === "following" && !q ? "Nothing from people you follow yet" : "No posts here yet"}</p>
              <p className="mt-1 text-sm text-muted-foreground">{tab === "following" && !q ? "Follow a few creators and their posts will land here." : "Try another category or search."}</p>
              <Link to="/creators"><Button className="mt-4 rounded-full">Find creators</Button></Link>
            </div>
          ) : grid ? (
            <div className="grid grid-cols-3 gap-1.5 sm:gap-2" data-testid="stage-grid">
              {feed.items.map((p) => <PostTile key={p.id} post={p} />)}
            </div>
          ) : (
            <div className="space-y-4" data-testid="stage-feed">
          {!q && !grid ? <NewPostsPill since={loadedAt} surface={tab === "following" ? "following" : "for_you"} onShow={() => { feed.reload(); window.scrollTo({ top: 0, behavior: "smooth" }); }} /> : null}
              {feed.items.map((p) => <PostCard key={p.id} post={p} />)}
            </div>
          )}
          {feed.hasMore && feed.state !== "loading" && (
            <div className="mt-6 text-center">
              <Button variant="outline" className="rounded-full px-8" onClick={feed.loadMore} disabled={feed.state === "more"} data-testid="stage-more">
                {feed.state === "more" ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </div>

        <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-2xl border border-border bg-card p-5">
            <h2 className="font-bold">Creators to follow</h2>
            <ul className="mt-3 space-y-3">
              {(creators?.items || []).slice(0, 6).map((c) => (
                <li key={c.id}>
                  <Link to={`/@${c.handle}`} className="flex items-center gap-3 rounded-xl hover:bg-muted/60">
                    <CreatorAvatar author={c} size={38} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1 truncate text-sm font-semibold">{c.display_name} <VerifiedMark author={c} size={13} /></span>
                      <span className="block truncate text-xs text-muted-foreground">@{c.handle}{c.categories?.[0] ? ` · ${c.categories[0]}` : ""}</span>
                    </span>
                  </Link>
                </li>
              ))}
              {creators === undefined && Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-9 rounded-xl" />)}
            </ul>
            <Link to="/creators" className="mt-3 inline-block text-sm font-medium text-primary hover:underline">Browse all creators</Link>
          </div>
          {Array.isArray(trending?.items) && trending.items.length > 0 && (
            <div className="rounded-2xl border border-border bg-card p-5">
              <h2 className="font-bold">Trending this week</h2>
              <ul className="mt-3 flex flex-wrap gap-2">
                {trending.items.map((t) => (
                  <li key={t.tag}>
                    <button type="button" onClick={() => { setSearch(`#${t.tag}`); set({ q: `#${t.tag}` }); }} className="rounded-full bg-muted px-3 py-1 text-sm font-medium hover:bg-muted/70">#{t.tag} <span className="text-xs text-muted-foreground">{t.posts}</span></button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="rounded-2xl p-5 text-white" style={{ background: "linear-gradient(135deg, var(--fable), #7C3AED)" }}>
            <p className="inline-flex items-center gap-2 text-sm font-semibold"><Smartphone size={16} /> Post, go live, join Circles</p>
            <p className="mt-1 text-sm text-white/85">Posting, Reels, Live and Backstage are in the Fable + app. The web is for watching, following and booking.</p>
            <Link to="/get-the-app" className="mt-3 inline-block rounded-full bg-white px-4 py-1.5 text-sm font-semibold text-foreground">Get the app</Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
